import React, {useEffect, useState, useCallback, useRef} from "react";
import {
    Box,
    Grid,
    Typography,
    Paper,
    Button,
    ButtonGroup,
    TextField,
    CircularProgress,
    Snackbar,
    Alert
} from "@mui/material";
import {LocalizationProvider, DatePicker} from "@mui/x-date-pickers";
import {AdapterDateFns} from "@mui/x-date-pickers/AdapterDateFns";
import CryptoJS from "crypto-js";
import {KEY} from "../../Constants";
import {analyticsApi, getLandlordId} from "../../services/api";
import SummaryCards from "./components/SummaryCards";
import PortfolioOverview from "./components/PortfolioOverview";
import PortfolioMetrics from "./components/PortfolioMetrics";
import DetailedAnalytics from "./components/DetailedAnalytics";
import RefreshIcon from '@mui/icons-material/Refresh';
import DownloadIcon from '@mui/icons-material/Download';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

export default function Analytics(props) {
    const [loading, setLoading] = useState(true);
    const [year, setYear] = useState(new Date());
    const [data, setData] = useState([]);
    const [portfolioData, setPortfolioData] = useState({
        properties: [],
        totalProperties: 0,
        totalUnits: 0,
        occupiedUnits: 0,
        occupancy: 0,
        expectedIncome: 0,
        receivedIncome: 0,
        depositHeld: 0,
        vacantUnits: Object.values({}).reduce((a, b) => a + b, 0),
        expensesTotal: Object.values({}).reduce((a, b) => a + b, 0),
        tenants: [],
        occupancyMap: {}
    });
    const [backgroundRefresh, setBackgroundRefresh] = useState(false);
    const [showBackgroundRefreshAlert, setShowBackgroundRefreshAlert] = useState(false);
    const [autoRefreshEnabled, setAutoRefreshEnabled] = useState(false);
    const autoRefreshTimerRef = useRef(null);
    const initialLoadCompletedRef = useRef(false);
const [originalText, ] = useState(() => {
    const encryptedData = localStorage.getItem("myMap");
    return encryptedData 
        ? CryptoJS.AES.decrypt(encryptedData, KEY).toString(CryptoJS.enc.Utf8)
        : "";
});

    // Extract the landlord ID once to avoid dependency issues
    const landlordIdRef = useRef(null);
    useEffect(() => {
        try {
            landlordIdRef.current = getLandlordId();
        } catch (error) {
            console.error("Error extracting landlord ID:", error);
        }
    }, []);

    // Extract property data from localStorage (only once)
    useEffect(() => {
        try {
            // Parse portfolio details from local storage
            const portfolioDetailsStr = localStorage.getItem("portfolioDetails");
            if (portfolioDetailsStr) {
                const decrypted = CryptoJS.AES.decrypt(portfolioDetailsStr, KEY).toString(CryptoJS.enc.Utf8);
                const portfolioItems = new Map(JSON.parse(decrypted));

                // Set portfolio summary data
                setPortfolioData({
                    properties: portfolioItems.get("properties") || [],
                    totalProperties: portfolioItems.get("number_of_properties") || 0,
                    totalUnits: portfolioItems.get("number_of_units") || 0,
                    occupiedUnits: portfolioItems.get("occupied_units") || 0,
                    occupancy: portfolioItems.get("occupancy") || 0,
                    expectedIncome: portfolioItems.get("expectedIncome") || 0,
                    receivedIncome: portfolioItems.get("receivedMoney") || 0,
                    depositHeld: portfolioItems.get("deposit_held")?.["Total deposit"] || 0,
                    vacantUnits: Object.values(portfolioItems.get("vacantUnitsMap") || {}).reduce((a, b) => a + b, 0),
                    expensesTotal: Object.values(portfolioItems.get("expensesMap") || {}).reduce((a, b) => a + b, 0),
                    tenants: portfolioItems.get("tenants") || [],
                    occupancyMap: portfolioItems.get("occupancyMap") || {}
                });
            }
        } catch (error) {
            console.error("Error loading portfolio data:", error);
        }
    }, []);

    // Function to fetch analytics data (useCallback to prevent recreation)
    const fetchAnalyticsData = useCallback(async (yearValue, isBackgroundRefresh = false) => {
        if (!landlordIdRef.current) return;

        if (isBackgroundRefresh) {
            setBackgroundRefresh(true);
        } else {
            setLoading(true);
        }

        try {
            const response = await analyticsApi.getAnalytics({
                landlord_id: landlordIdRef.current,
                year: yearValue,
            });

            if (response.ok) {
                // Enhance the data for better visualization
                const enhancedData = response.data.map(month => ({
                    ...month,
                    profit: month.transactions - month.expenses,
                    profitMargin: month.transactions > 0
                        ? ((month.transactions - month.expenses) / month.transactions * 100).toFixed(1)
                        : 0
                }));
                setData(enhancedData);

                if (isBackgroundRefresh) {
                    setBackgroundRefresh(false);
                    setShowBackgroundRefreshAlert(true);
                    setTimeout(() => setShowBackgroundRefreshAlert(false), 3000);
                } else {
                    setLoading(false);
                    props.snackBar({
                        text: "Data Retrieved Successfully",
                        type: "success",
                    });
                }
            } else {
                if (isBackgroundRefresh) {
                    setBackgroundRefresh(false);
                } else {
                    setLoading(false);
                    props.snackBar({
                        text: "Something Went Wrong. Try Again Later",
                        type: "error",
                    });
                }
            }
        } catch (err) {
            console.error(err);
            if (isBackgroundRefresh) {
                setBackgroundRefresh(false);
            } else {
                setLoading(false);
                props.snackBar({
                    text: "Something Went Wrong. Call Customer Care For Assistance.",
                    type: "error",
                });
            }
        }
    }, [props]);

    // Initial fetch when component mounts or year changes
    useEffect(() => {
        if (!initialLoadCompletedRef.current && landlordIdRef.current) {
            props.refreshSideBar();
            fetchAnalyticsData(year.getFullYear());
            initialLoadCompletedRef.current = true; // Mark initial load as completed
        }
    }, [fetchAnalyticsData, props, year, landlordIdRef.current]);

    // Setup auto-refresh timer
    useEffect(() => {
        // Clear any existing timer
        if (autoRefreshTimerRef.current) {
            clearInterval(autoRefreshTimerRef.current);
        }

        // Setup new timer if auto-refresh is enabled
        if (autoRefreshEnabled) {
            autoRefreshTimerRef.current = setInterval(() => {
                fetchAnalyticsData(year.getFullYear(), true); // true = background refresh
            }, 5 * 60 * 1000); // 5 minutes in milliseconds
        }

        // Cleanup on component unmount
        return () => {
            if (autoRefreshTimerRef.current) {
                clearInterval(autoRefreshTimerRef.current);
            }
        };
    }, [autoRefreshEnabled, fetchAnalyticsData, year]);

    // Handle year change
    const handleYearChange = (newValue) => {
        if (newValue && newValue !== year) {
            setYear(newValue);
            fetchAnalyticsData(newValue.getFullYear());
        }
    };

    return (
        <Box>
            {/* Background refresh notification */}
            <Snackbar
                open={showBackgroundRefreshAlert}
                autoHideDuration={3000}
                onClose={() => setShowBackgroundRefreshAlert(false)}
                anchorOrigin={{vertical: 'bottom', horizontal: 'right'}}
            >
                <Alert
                    icon={<CheckCircleIcon fontSize="inherit"/>}
                    severity="success"
                    variant="filled"
                    sx={{
                        width: '100%',
                        boxShadow: '0 3px 10px rgba(0,0,0,0.2)'
                    }}
                >
                    Data refreshed in background
                </Alert>
            </Snackbar>

            {/* Header */}
            <Paper
                elevation={0}
                sx={{
                    p: 3,
                    mb: 3,
                    borderRadius: 2,
                    background: `linear-gradient(45deg, #1976d2 0%, #42a5f5 100%)`,
                    color: 'white'
                }}
            >
                <Grid container alignItems="center" spacing={3}>
                    <Grid item xs={12} md={8}>
                        <Typography variant="h4" fontWeight="bold" sx={{mb: 1}}>
                            Analytics Dashboard
                        </Typography>
                        <Typography variant="body1">
                            Comprehensive overview of your property portfolio performance
                        </Typography>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <Box display="flex" justifyContent={{xs: 'flex-start', md: 'flex-end'}} alignItems="center"
                             gap={2}>
                            <LocalizationProvider dateAdapter={AdapterDateFns}>
                                <DatePicker
                                    disableFuture
                                    label="Select Year"
                                    openTo="year"
                                    views={["year"]}
                                    value={year}
                                    onChange={handleYearChange}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            size="small"
                                            sx={{
                                                width: 150,
                                                bgcolor: 'rgba(255,255,255,0.9)',
                                                borderRadius: 1,
                                                '& .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: 'transparent'
                                                }
                                            }}
                                        />
                                    )}
                                />
                            </LocalizationProvider>
                            <ButtonGroup variant="contained" size="small" aria-label="dashboard actions">
                                <Button
                                    startIcon={<RefreshIcon/>}
                                    onClick={() => fetchAnalyticsData(year.getFullYear())}
                                    disabled={loading || backgroundRefresh}
                                    sx={{
                                        bgcolor: 'rgba(255,255,255,0.2)',
                                        '&:hover': {
                                            bgcolor: 'rgba(255,255,255,0.3)'
                                        }
                                    }}
                                >
                                    {backgroundRefresh ? 'Refreshing...' : 'Refresh'}
                                </Button>
                                <Button
                                    startIcon={<DownloadIcon/>}
                                    onClick={() => {
                                        // Export logic would go here
                                        props.snackBar({
                                            text: "Report downloaded successfully",
                                            type: "success",
                                        });
                                    }}
                                    sx={{
                                        bgcolor: 'rgba(255,255,255,0.2)',
                                        '&:hover': {
                                            bgcolor: 'rgba(255,255,255,0.3)'
                                        }
                                    }}
                                >
                                    Export
                                </Button>
                            </ButtonGroup>
                        </Box>
                    </Grid>
                </Grid>
            </Paper>

            {/* Loading indicator */}
            {loading ? (
                <Box display="flex" justifyContent="center" alignItems="center" sx={{py: 8}}>
                    <CircularProgress/>
                    <Typography variant="body1" sx={{ml: 2}}>Loading analytics data...</Typography>
                </Box>
            ) : (
                <>
                    {/* Summary Cards */}
                    <SummaryCards data={data}/>

                    {/* Portfolio Overview and Metrics */}
                    <Grid container spacing={3} sx={{mb: 3}}>
                        <Grid item xs={12} md={8}>
                            <PortfolioOverview data={data}/>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <PortfolioMetrics portfolioData={portfolioData}/>
                        </Grid>
                    </Grid>

                    {/* Detailed Analytics */}
                    <DetailedAnalytics data={data} portfolioData={portfolioData}/>
                </>
            )}
        </Box>
    );
}